import { Component, ReactNode } from 'react';

type Props = { children: ReactNode };
type State = { hasError: boolean; err?: unknown; errorInfo?: React.ErrorInfo };

/**
 * ErrorBoundary - Prevents white screen by catching React errors
 * Displays a user-friendly error message while keeping the app usable
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(err: unknown): State {
    return { hasError: true, err };
  }

  componentDidCatch(err: unknown, errorInfo: React.ErrorInfo) {
    console.error('🚨 [ErrorBoundary] UI Error caught:', err);
    console.error('🚨 [ErrorBoundary] Error info:', errorInfo);
    console.error('🚨 [ErrorBoundary] Component stack:', errorInfo.componentStack);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          minHeight: '100vh', 
          padding: '24px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#0f172a',
          color: '#f9fafb'
        }}>
          <div style={{ 
            maxWidth: '600px', 
            width: '100%',
            padding: '24px',
            backgroundColor: '#1e293b',
            borderRadius: '8px',
            border: '1px solid #334155'
          }}>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px', color: '#f9fafb' }}>
              ⚠️ Something went wrong
            </h1>
            <p style={{ marginBottom: '16px', color: '#cbd5e1' }}>
              The application encountered an error. The app stays usable while we fix it.
            </p>
            <p style={{ marginBottom: '16px', fontSize: '14px', color: '#94a3b8' }}>
              Open the browser console (F12) for detailed error information.
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, err: undefined, errorInfo: undefined });
                window.location.reload();
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Reload Page
            </button>
            {import.meta.env.DEV && this.state.err && (
              <details style={{ marginTop: '16px' }}>
                <summary style={{ cursor: 'pointer', color: '#94a3b8', fontSize: '12px', marginBottom: '8px' }}>
                  Error details (dev only)
                </summary>
                <pre style={{
                  fontSize: '11px',
                  color: '#ef4444',
                  backgroundColor: '#1e293b',
                  padding: '12px',
                  borderRadius: '4px',
                  overflow: 'auto',
                  maxHeight: '200px',
                  border: '1px solid #334155'
                }}>
                  {String(this.state.err)}
                  {this.state.errorInfo?.componentStack && (
                    <div style={{ marginTop: '8px', color: '#64748b' }}>
                      {this.state.errorInfo.componentStack}
                    </div>
                  )}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

